import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, ConfirmDialog, Field, Modal, Textarea } from '@bb/ui';
import { ApiError, type AdminApi, type CanvasView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';

export type LifecycleAction = 'pause_entries' | 'pause_all' | 'resume' | 'stop';

/**
 * What each status allows (`@bb/journeys` transitions): a running Canvas pauses new entries or everything, a paused one
 * resumes, and any running or paused one stops. From «paused everything» only resume or stop are offered.
 */
export const LIFECYCLE_ACTIONS: Record<CanvasView['status'], LifecycleAction[]> = {
  draft: [],
  active: ['pause_entries', 'pause_all', 'stop'],
  paused_entries: ['resume', 'pause_all', 'stop'],
  paused_all: ['resume', 'stop'],
  stopped: [],
  archived: [],
};

const LABELS: Record<LifecycleAction, string> = { pause_entries: 'Pause new entries', pause_all: 'Pause everything', resume: 'Resume', stop: 'Stop' };
const MAX_REASON = 500;

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** What a confirmed action does, in plain words (docs/features/canvas-runtime.md, «Lifecycle»). */
function explanation(action: Exclude<LifecycleAction, 'stop'>, status: CanvasView['status']) {
  switch (action) {
    case 'pause_entries':
      return 'New users cannot enter: triggers, scheduled runs and API requests are refused. Users already in the Canvas continue their journey and get its messages. Resume to let new users in again.';
    case 'pause_all':
      return 'Nothing moves until you resume: new users cannot enter, delays and waits freeze and no messages are sent. Events that happen during the pause are not kept for later. On resume, delays that ran out continue at once.';
    case 'resume':
      return status === 'paused_all' ? 'Delays and waits that ran out during the pause continue at once, the rest as planned. New users can enter again.' : 'New users can enter again.';
  }
}

/**
 * Pause, resume and stop of a Canvas (BB-110 over the BB-121 runtime) as a toolbar; each asks for confirmation and
 * stop asks for a reason (audit). Needs `messages.publish`; the badge follows the status the server answers.
 */
export function CanvasLifecycleActions({ environmentId, canvas }: { environmentId: string; canvas: CanvasView }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const canPublish = usePermissions().has('messages.publish');
  const [confirm, setConfirm] = useState<LifecycleAction | null>(null);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: async (action: LifecycleAction): Promise<{ view: CanvasView; notice: string }> => {
      switch (action) {
        case 'pause_entries':
          return { view: await api.pauseCanvas(environmentId, canvas.id, 'entries'), notice: 'New entries are paused.' };
        case 'pause_all':
          return { view: await api.pauseCanvas(environmentId, canvas.id, 'all'), notice: 'The Canvas is paused.' };
        case 'resume': {
          const { resumed, ...view } = await api.resumeCanvas(environmentId, canvas.id);
          const now = resumed.timers || resumed.entries ? ` ${resumed.timers} overdue ${resumed.timers === 1 ? 'delay' : 'delays'} and ${resumed.entries} ${resumed.entries === 1 ? 'entry' : 'entries'} continue now.` : '';
          return { view, notice: `The Canvas is running again.${now}` };
        }
        case 'stop':
          return { view: await api.stopCanvas(environmentId, canvas.id, reason.trim()), notice: 'The Canvas is stopped.' };
      }
    },
    onSuccess: async ({ view, notice: text }) => {
      setConfirm(null);
      setReason('');
      setNotice(text);
      queryClient.setQueryData(['canvases', environmentId, 'one', canvas.id], view);
      await queryClient.invalidateQueries({ queryKey: ['canvases', environmentId] });
    },
  });
  const actions = canPublish ? LIFECYCLE_ACTIONS[canvas.status] : [];
  if (actions.length === 0 && !notice) return null;
  const open = (action: LifecycleAction) => {
    run.reset();
    setNotice(null);
    setConfirm(action);
  };
  const close = (isOpen: boolean) => {
    if (!isOpen) {
      setConfirm(null);
      setReason('');
    }
  };
  const pending = confirm && confirm !== 'stop' ? confirm : null;
  return (
    <>
      {actions.length > 0 && (
        <div className="toolbar" role="group" aria-label={`Run controls for ${canvas.name}`}>
          {actions.map((action) => (
            <Button key={action} variant={action === 'stop' ? 'danger' : 'ghost'} disabled={run.isPending} onClick={() => open(action)}>
              {LABELS[action]}
            </Button>
          ))}
        </div>
      )}
      {notice && <p role="status">{notice}</p>}
      {run.isError && !confirm && <p role="alert">{errorText(run.error)}</p>}
      {pending && (
        <ConfirmDialog
          open
          onOpenChange={close}
          title={pending === 'pause_entries' ? `Pause new entries of “${canvas.name}”?` : pending === 'pause_all' ? `Pause everything in “${canvas.name}”?` : `Resume “${canvas.name}”?`}
          confirmLabel={LABELS[pending]}
          tone="primary"
          busy={run.isPending}
          onConfirm={() => run.mutate(pending)}
        >
          <p>{explanation(pending, canvas.status)}</p>
          {run.isError && <p role="alert">{errorText(run.error)}</p>}
        </ConfirmDialog>
      )}
      <Modal
        open={confirm === 'stop'}
        onOpenChange={close}
        title={`Stop “${canvas.name}”?`}
        description="Stopping ends the Canvas for good: delays and waits are cancelled, everyone still in it leaves, and nothing more is sent. Messages already delivered are not recalled. A stopped Canvas cannot be resumed or launched again; duplicate it to start over."
        footer={
          <>
            <Button variant="ghost" onClick={() => close(false)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={!reason.trim() || reason.trim().length > MAX_REASON} loading={run.isPending} onClick={() => run.mutate('stop')}>
              Stop Canvas
            </Button>
          </>
        }
      >
        <Field label="Reason" hint="Required; it goes to the audit log and shows on the Canvas page.">
          <Textarea value={reason} maxLength={MAX_REASON} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {run.isError && confirm === 'stop' && <p role="alert">{errorText(run.error)}</p>}
      </Modal>
    </>
  );
}
